/**
 * @file TemplateMultiSelect.js
 * @description A multi-select dropdown component for selecting templates.
 */

import React, { useState, useEffect } from "react";
import { SearchableDropdown } from "../../helpers/SearchableDropdown_style_b_v2";
import { fetchTemplates } from "../../../services/templateService";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * TemplateMultiSelect component represents a searchable multi-select dropdown for templates.
 *
 * @component
 * @param {string} className - The CSS class name for the component.
 * @param {Array} selectedTemplates - The array of selected template objects.
 * @param {Function} onTemplatesChange - The callback function triggered when the selected templates change.
 * @param {string} [label="Templates"] - The label text for the dropdown.
 * @param {boolean} [isRequired=false] - Specifies whether the dropdown is required.
 * @param {boolean} resetTrigger - The trigger to reset the dropdown.
 * @param {string} tenantId - The ID of the tenant.
 * @param {boolean} [showSelectedItemsInField=true] - Specifies whether to show the selected items in the input field.
 * @param {boolean} [showSelectionBox=true] - Specifies whether to show the selection box.
 * @returns {JSX.Element} The rendered TemplateMultiSelect component.
 */
const TemplateMultiSelect = ({
  className,
  selectedTemplates,
  onTemplatesChange,
  label = "Templates",
  isRequired = false,
  resetTrigger,
  tenantId,
  showSelectedItemsInField = true,
  showSelectionBox = true,
  showSelectedCount = false,
}) => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setIsLoading(true);
        const data = await fetchTemplates(tenantId);
        const cleanedData = data.viewModels.map(({ id, name }) => ({
          id,
          name,
        }));
        setTemplates(cleanedData);
      } catch (error) {
        console.error("Could not fetch templates:", error);
        Toast.error(getText('msg_47ac7ce5c0b6'));
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, [tenantId]);

  const handleTemplatesChange = (selectedOptions) => {
    onTemplatesChange(selectedOptions);
  };

  return (
    <div className={`form-group mb-3 ${className}`}>
      <label htmlFor="template-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <SearchableDropdown
        id="template-dropdown"
        options={templates}
        optionIDField="id"
        updateOptions={handleTemplatesChange}
        savedOptions={selectedTemplates}
        savedOptionIDField="id"
        disabled={templates.length === 0}
        isLoading={isLoading}
        resetTrigger={resetTrigger}
        showSelectedItemsInField={showSelectedItemsInField}
        showSelectionBox={showSelectionBox}
        showSelectedCount={showSelectedCount}
        allSelectedLabel={getText('msg_f25b700ed9f0')}
      />
    </div>
  );
};

export default TemplateMultiSelect;
